import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, FileText, Globe, LogOut, Menu, Moon, Pencil, Sun, User, X } from "lucide-react";
import styles from "./PortalHeader.module.css";
import Logo from "./Logo";
import DiscordIcon from "../icons/DiscordIcon";
import Button from "./Button";
import { useLanguage } from "../LanguageContext";
import { useAuth } from "../AuthContext";

const LANGUAGES = ["ENGLISH", "SPANISH", "PORTUGUESE (BR)", "FRENCH", "ARABIC"];

const DISCORD_URL = "https://discord.com/invite/8z37khrMA6";

// `href` renders a link that opens in a new tab instead of a button
function HeaderIcon({ children, label, onClick, active, href }) {
  if (href) {
    return (
      <a className={styles.iconButton} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
        {children}
      </a>
    );
  }
  return (
    <button
      type="button"
      className={`${styles.iconButton} ${active ? styles.iconButtonActive : ""}`}
      aria-label={label}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

// `onSave` resolves once the name is saved, or throws the message to show
function EditNameModal({ name, onClose, onSave }) {
  const { t } = useLanguage();
  const [value, setValue] = useState(name);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(value);
      onClose();
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className={styles.modalOverlay} onMouseDown={onClose}>
      <div
        className={styles.modalCard}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t("header_edit_name")}
      >
        <div className={styles.modalHeader}>
          <span className={styles.modalTitle}>{t("header_edit_name")}</span>
          <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Close">
            <X size={21} />
          </button>
        </div>
        <div className={styles.modalBody}>
          <input
            className={styles.modalInput}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); }}
            maxLength={32}
            autoFocus
          />
          <Button
            type="button"
            className={styles.modalSave}
            onClick={save}
            disabled={saving || !value.trim()}
          >
            {t("header_save_change")}
          </Button>
        </div>
        {error ? <p className={styles.modalError} role="alert">{error}</p> : null}
      </div>
    </div>,
    document.body
  );
}

function UserPopup({ onClose, onEditName, userName, email, effectiveTheme, toggleTheme }) {
  const { t } = useLanguage();
  return (
    <div className={styles.userPopup} role="dialog" aria-label="User menu">
      <div className={styles.userPopupProfile}>
        <div className={styles.userPopupName}>
          <span>{userName}</span>
          <button
            type="button"
            className={styles.userPopupEdit}
            aria-label="Edit profile"
            onClick={() => { onClose(); onEditName(); }}
          >
            <Pencil size={14} />
          </button>
        </div>
        <div className={styles.userPopupEmail}>{email}</div>
      </div>

      <div className={styles.userPopupDivider} />

      <nav className={styles.userPopupNav}>
        <button
          type="button"
          className={styles.userPopupItem}
          onClick={() => { toggleTheme(); onClose(); }}
        >
          {effectiveTheme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          <span>{t("header_switch_theme")}</span>
        </button>
        <a
          href="https://docs.propfunding.com"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.userPopupItem}
          onClick={onClose}
        >
          <FileText size={18} />
          <span>{t("header_docs")}</span>
        </a>
      </nav>
    </div>
  );
}

function LangPopup({ onClose }) {
  const { t, langName, setLangName } = useLanguage();
  return (
    <div className={styles.userPopup} role="dialog" aria-label="Language menu">
      <div className={styles.langPopupHeader}>
        <span className={styles.langPopupTitle}>{t("header_language")}</span>
      </div>
      <div className={styles.userPopupDivider} />
      <nav className={styles.langPopupNav}>
        {LANGUAGES.map((lang) => (
          <button
            key={lang}
            type="button"
            className={`${styles.langOption} ${langName === lang ? styles.langOptionActive : ""}`}
            onClick={() => { setLangName(lang); onClose(); }}
          >
            <span className={styles.langCheck}>
              {langName === lang && <Check size={14} strokeWidth={2.5} />}
            </span>
            {lang}
          </button>
        ))}
      </nav>
    </div>
  );
}

export default function PortalHeader({ activePath, navItems, onNavigate, logoOnly = false, effectiveTheme, toggleTheme }) {
  const { t } = useLanguage();
  const [userPopupOpen, setUserPopupOpen] = useState(false);
  const [langPopupOpen, setLangPopupOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout, updateProfile } = useAuth();
  const userName = user.username || user.email;
  const userRef = useRef(null);
  const langRef = useRef(null);

  useEffect(() => {
    if (!userPopupOpen) return;
    function handleClick(e) {
      if (userRef.current && !userRef.current.contains(e.target)) {
        setUserPopupOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [userPopupOpen]);

  useEffect(() => {
    if (!langPopupOpen) return;
    function handleClick(e) {
      if (langRef.current && !langRef.current.contains(e.target)) {
        setLangPopupOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [langPopupOpen]);

  useEffect(() => {
    if (mobileMenuOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      window.scrollTo(0, parseInt(scrollY || "0") * -1);
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
    };
  }, [mobileMenuOpen]);

  return (
    <>
      <header className={`${styles.header} ${logoOnly ? styles.headerLogoOnly : ""} d-grid`}>
        <button
          type="button"
          className={styles.brand}
          onClick={() => onNavigate("/portfolio")}
          aria-label="Go to portfolio"
        >
          <Logo className={styles.logo} aria-hidden="true" />
        </button>

        {!logoOnly && <nav className={styles.nav} aria-label="Portal navigation">
          {navItems.map((item) => (
            <a
              key={item.path}
              href={item.path}
              className={`${styles.navLink} ${activePath === item.path ? styles.navLinkActive : ""}`}
              onClick={(event) => {
                event.preventDefault();
                onNavigate(item.path);
              }}
            >
              {item.label}
            </a>
          ))}
        </nav>}

        {!logoOnly && <div className={styles.actions}>
          <div className={styles.userWrapper} ref={userRef}>
            <HeaderIcon
              label="Profile"
              active={userPopupOpen}
              onClick={() => { setLangPopupOpen(false); setUserPopupOpen((o) => !o); }}
            >
              <User size={16} />
            </HeaderIcon>
            {userPopupOpen && (
              <UserPopup
                userName={userName}
                email={user.email}
                onClose={() => setUserPopupOpen(false)}
                onEditName={() => setEditModalOpen(true)}
                effectiveTheme={effectiveTheme}
                toggleTheme={toggleTheme}
              />
            )}
          </div>
          <div className={styles.userWrapper} ref={langRef}>
            <HeaderIcon
              label="Language"
              active={langPopupOpen}
              onClick={() => { setUserPopupOpen(false); setLangPopupOpen((o) => !o); }}
            >
              <Globe size={16} />
            </HeaderIcon>
            {langPopupOpen && (
              <LangPopup onClose={() => setLangPopupOpen(false)} />
            )}
          </div>
          <HeaderIcon label="Discord" href={DISCORD_URL}>
            <DiscordIcon />
          </HeaderIcon>
          <HeaderIcon label="Log out" onClick={logout}>
            <LogOut size={16} />
          </HeaderIcon>
        </div>}

        {!logoOnly && (
          <button
            type="button"
            className={styles.menuBtn}
            onClick={() => setMobileMenuOpen((o) => !o)}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        )}

        {editModalOpen && (
          <EditNameModal
            name={userName}
            onClose={() => setEditModalOpen(false)}
            onSave={(username) => updateProfile({ username })}
          />
        )}
      </header>

      {!logoOnly && mobileMenuOpen && createPortal(
        <div className={styles.mobileMenu} role="dialog" aria-modal="true" aria-label="Navigation menu">
          <nav className={styles.mobileMenuNav}>
            {navItems.map((item) => (
              <div key={item.path} className={styles.mobileMenuItemWrap}>
                <a
                  href={item.path}
                  className={`${styles.mobileMenuItem} ${activePath === item.path ? styles.mobileMenuItemActive : ""}`}
                  onClick={(e) => {
                    e.preventDefault();
                    setMobileMenuOpen(false);
                    onNavigate(item.path);
                  }}
                >
                  {item.label}
                </a>
                <div className={styles.mobileMenuDivider} />
              </div>
            ))}
            <div className={styles.mobileMenuItemWrap}>
              <a
                href="/account"
                className={`${styles.mobileMenuItem} ${activePath === "/account" ? styles.mobileMenuItemActive : ""}`}
                onClick={(e) => {
                  e.preventDefault();
                  setMobileMenuOpen(false);
                  onNavigate("/account");
                }}
              >
                {t("nav_account")}
              </a>
              <div className={styles.mobileMenuDivider} />
            </div>
          </nav>

          <div className={styles.mobileMenuFooter}>
            <button
              type="button"
              className={styles.mobileMenuThemeRow}
              onClick={toggleTheme}
              aria-label={effectiveTheme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              <span className={styles.mobileMenuThemeLabel}>{t("menu_dark_mode")}</span>
              <span className={`${styles.themeToggleTrack} ${effectiveTheme === 'dark' ? styles.themeToggleOn : ''}`}>
                <span className={styles.themeToggleKnob} />
              </span>
            </button>
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={`${styles.mobileMenuActionBtn} ${styles.mobileMenuDiscordBtn}`}
            >
              <DiscordIcon size={18} color="currentColor" />
              <span>{t("menu_join_discord")}</span>
            </a>
            <button type="button" className={styles.mobileMenuActionBtn} onClick={logout}>
              <LogOut size={18} />
              <span>{t("menu_logout")}</span>
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
